import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2, Download, Images, Loader2, RefreshCw, Save, Search, Trash2, Upload, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PasteImageButton from "@/components/PasteImageButton";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Newsdesk Stock — the editors' own photo library (Cloudflare R2).
 *
 * Browse / search the catalogue, upload new photos (several at once,
 * with shared keywords + credit), and open any photo to edit its title,
 * keywords, preset caption/credit and description, replace its file
 * with a new version, approve it (Pending tab) or delete it.
 *
 * Photos also arrive from an article's source images ("Save to Stock"
 * on a source tile — live immediately, with the source URL kept).
 *
 * "Pending" photos came from an article upload with "Add this photo to
 * Newsdesk Stock" ticked; they go live on their own when that article
 * is published or scheduled, or can be approved here by hand.
 *
 * The library is shared by every property; each photo shows which
 * property uploaded it. Search is keyword-first, accent-insensitive and
 * handles plurals (backend/stock_library.py `rank`).
 */

const PAGE = 60;
const SORTS = [
  { value: "newest", get label() { return tr("Newest"); } },
  { value: "oldest", get label() { return tr("Oldest"); } },
  { value: "most_used", get label() { return tr("Most used"); } },
  { value: "updated", get label() { return tr("Recently edited"); } },
];

function formatBytes(n) {
  if (!n) return "";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(uiLocale(), { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function sourceHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function splitKeywords(text) {
  const seen = new Set();
  return String(text || "")
    .split(/[,;\n]+/)
    .map((k) => k.trim().replace(/^#/, "").toLowerCase())
    .filter((k) => {
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

export default function StockLibraryPage() {
  const { user } = useAuth();
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState("newest");
  const [limit, setLimit] = useState(PAGE);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Images pasted on the page itself (dialog closed): they open the
  // Upload dialog pre-loaded, so copy → Cmd/Ctrl+V → add keywords.
  const [pastedFiles, setPastedFiles] = useState([]);
  const [openId, setOpenId] = useState(null);

  // Debounced search-as-you-type.
  useEffect(() => {
    const t = setTimeout(() => setQuery(draft.trim()), 300);
    return () => clearTimeout(t);
  }, [draft]);
  useEffect(() => {
    setLimit(PAGE);
  }, [query, status, sort]);

  const statusQuery = useQuery({
    queryKey: ["stock-library-status"],
    queryFn: api.stockLibraryStatus,
    staleTime: 10 * 60 * 1000,
  });
  const configured = !!statusQuery.data?.configured;

  const listQuery = useQuery({
    queryKey: ["stock-library", { query, status, sort, limit }],
    queryFn: () => api.listStockLibrary({ q: query, status, sort, limit }),
    enabled: configured,
    placeholderData: keepPreviousData,
  });

  const items = listQuery.data?.items || [];
  const total = listQuery.data?.total || 0;
  const counts = listQuery.data?.counts || { active: 0, pending: 0 };
  const propertyName = useMemo(() => {
    const m = new Map((user?.properties || []).map((p) => [p.id, p.name]));
    return (id) => m.get(id) || "";
  }, [user]);

  return (
    <div className="flex-1 min-w-0 overflow-y-auto" data-testid="stock-library-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="overline">{tr("Newsroom · Photos")}</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight mt-1 flex items-center gap-2">
              <Images className="w-7 h-7 text-[var(--accent)] shrink-0" />{tr("Newsdesk Stock")}</h1>
            <p className="text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">{tr("Your own photos, searchable by keyword and ready to use in any article from Photo recommendations or the body-image Stock tab. Shared by every property.")}</p>
          </div>
          {configured && (
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <PasteImageButton
                testId="stock-paste-open"
                label={tr("Paste")}
                title={tr("Paste an image from the clipboard (Cmd/Ctrl+V anywhere on this page)")}
                // Page-level hotkey only while nothing is open on top:
                // the Upload dialog registers its own paste target, and
                // a paste over the photo editor must not start an upload.
                hotkey={!uploadOpen && !openId}
                onImage={(file) => {
                  setPastedFiles([file]);
                  setUploadOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
                iconClass="w-4 h-4"
              />
              <button
                data-testid="stock-upload-open"
                onClick={() => {
                  setPastedFiles([]);
                  setUploadOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[var(--ink)] text-white hover:opacity-90"
              >
                <Upload className="w-4 h-4" />{" "}{tr("Upload photos")}</button>
            </div>
          )}
        </div>

        {statusQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />{" "}{tr("Loading…")}</div>
        )}
        {statusQuery.isSuccess && !configured && (
          <div className="border border-[var(--border)] bg-white p-5 text-sm" data-testid="stock-not-configured">
            <div className="font-medium mb-1">{tr("Newsdesk Stock isn't connected to R2 yet.")}</div>
            <div className="text-[var(--ink-muted)]">{trRich("Missing backend settings: {list}. Add them to <0>backend/.env</0> and restart the backend.", { list: (statusQuery.data?.missing || []).join(", ") || "—" }, [<code />])}</div>
          </div>
        )}

        {configured && (
          <div className="border border-[var(--border)] bg-white">
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
                <input
                  data-testid="stock-search"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={tr("Search keywords, titles, credits… (e.g. malecón sunset)")}
                  className="w-full text-sm pl-8 pr-8 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
                />
                {draft && (
                  <button
                    type="button"
                    onClick={() => setDraft("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                    title={tr("Clear search")}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="flex gap-1 text-xs" role="tablist">
                {[
                  { key: "active", label: tr("Active ({active})", { active: counts.active }) },
                  { key: "pending", label: tr("Pending ({pending})", { pending: counts.pending }) },
                  { key: "all", label: tr("All") },
                ].map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={status === t.key}
                    data-testid={`stock-status-${t.key}`}
                    onClick={() => setStatus(t.key)}
                    className={
                      "px-3 py-1.5 border " +
                      (status === t.key
                        ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                        : "border-[var(--border)] hover:border-[var(--ink)]")
                    }
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <select
                data-testid="stock-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                disabled={!!query}
                title={query ? tr("Search results are ordered by relevance") : tr("Sort")}
                className="text-xs border border-[var(--border)] px-2 py-1.5 bg-white disabled:opacity-50"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {status === "pending" && (
              <div className="px-4 py-2 border-b border-[var(--border)] text-xs text-[var(--ink-muted)]">{tr("Photos added from an article upload. They go live automatically when that article is published or scheduled — or approve them here.")}</div>
            )}

            <div className="p-4">
              {listQuery.isLoading && (
                <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
                  <Loader2 className="w-4 h-4 animate-spin" />{" "}{tr("Loading photos…")}</div>
              )}
              {listQuery.isError && (
                <div className="text-sm text-red-700">
                  {listQuery.error?.response?.data?.detail || tr("Could not load the library")}
                </div>
              )}
              {listQuery.isSuccess && items.length === 0 && (
                <div className="text-sm text-[var(--ink-muted)] italic py-10 text-center">
                  {query
                    ? tr("No photos match that search.")
                    : status === "pending"
                      ? tr("Nothing pending.")
                      : tr("No photos yet — upload your first ones.")}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {items.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`stock-tile-${p.id}`}
                    onClick={() => setOpenId(p.id)}
                    className="text-left border border-[var(--border)] hover:border-[var(--ink)] bg-white group"
                  >
                    <div className="relative aspect-[4/3] bg-[var(--secondary)] overflow-hidden">
                      <img
                        src={p.thumb_url}
                        alt={p.alt || p.title || ""}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      {p.status === "pending" && (
                        <span className="absolute top-1 left-1 text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 bg-black/45 text-white">{tr("Pending")}</span>
                      )}
                      {p.use_count > 0 && (
                        <span className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 bg-black/45 text-white">{tr("Used {use_count}×", { use_count: p.use_count })}</span>
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-xs font-medium truncate">
                        {p.title || (p.keywords || []).slice(0, 3).join(", ") || p.file?.filename || tr("Untitled")}
                      </div>
                      <div className="text-[10px] text-[var(--ink-muted)] truncate">
                        {p.credit || tr("No credit set")}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {items.length < total && (
                <div className="mt-4 text-center">
                  <button
                    data-testid="stock-load-more"
                    onClick={() => setLimit((n) => n + PAGE)}
                    disabled={listQuery.isFetching}
                    className="text-xs px-4 py-1.5 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
                  >
                    {listQuery.isFetching ? tr("Loading…") : tr("Load more ({value} left)", { value: total - items.length })}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {uploadOpen && (
        <UploadDialog
          initialFiles={pastedFiles}
          onClose={() => {
            setUploadOpen(false);
            setPastedFiles([]);
          }}
        />
      )}
      {openId && (
        <PhotoDetail
          photoId={openId}
          propertyName={propertyName}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload (several files, shared keywords + credit)
// ---------------------------------------------------------------------------

function UploadDialog({ onClose, initialFiles = [] }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [files, setFiles] = useState(() => [...initialFiles]);
  const [keywords, setKeywords] = useState("");
  const [credit, setCredit] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [files],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const addFiles = (list) => {
    const imgs = Array.from(list || []).filter((f) => f.type?.startsWith("image/"));
    if (imgs.length < (list?.length || 0)) toast.error(tr("Only image files can be added"));
    setFiles((prev) => [...prev, ...imgs]);
  };

  const uploadAll = async () => {
    if (!files.length) return;
    setBusy(true);
    setDone(0);
    let added = 0;
    let dupes = 0;
    const failed = [];
    for (const f of files) {
      try {
        const res = await api.uploadStockLibraryPhoto(f, {
          keywords,
          credit,
          title: files.length === 1 ? title : "",
        });
        if (res?.duplicate) dupes += 1;
        else added += 1;
      } catch (e) {
        failed.push(`${f.name}: ${e?.response?.data?.detail || e?.message || tr("failed")}`);
      }
      setDone((n) => n + 1);
    }
    setBusy(false);
    qc.invalidateQueries({ queryKey: ["stock-library"] });
    if (added) toast.success(tr("{added} photo{s} added to Newsdesk Stock", { added, s: added === 1 ? "" : "s" }));
    if (dupes) toast(tr("{dupes} already in the library — skipped", { dupes }));
    if (failed.length) {
      failed.forEach((m) => toast.error(m));
      setFiles((prev) => prev.filter((f) => failed.some((m) => m.startsWith(`${f.name}:`))));
      return;
    }
    onClose();
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center px-4"
      onClick={() => !busy && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="stock-upload-dialog"
        className="w-full max-w-2xl bg-white border border-[var(--border)] shadow-xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">{tr("Newsdesk Stock")}</div>
            <h2 className="font-serif-display text-lg">{tr("Upload photos")}</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[var(--ink-muted)] hover:text-[var(--ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            data-testid="stock-upload-pick"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
            disabled={busy}
            className={
              "w-full py-6 border-2 border-dashed flex flex-col items-center justify-center gap-1.5 disabled:opacity-40 " +
              (dragOver ? "border-[var(--ink)] bg-[var(--secondary)]" : "border-[var(--border)] hover:border-[var(--ink)]")
            }
          >
            <Upload className="w-6 h-6" />
            {trRich("<0>Click, drop or paste images here</0><1>JPG, PNG or WebP · up to 25 MB · kept at full resolution (max 6000px)</1>", null, [<span className="text-xs" />, <span className="text-[10px] text-[var(--ink-muted)]" />])}
          </button>

          <div className="flex items-center gap-2 text-[11px] text-[var(--ink-muted)]">
            <PasteImageButton
              testId="stock-upload-paste"
              label={tr("Paste image")}
              onImage={(file) => addFiles([file])}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-40"
            />
            {trRich("<0>or press Cmd/Ctrl+V — each paste adds one photo.</0>", null, [<span />])}
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {previews.map((p, i) => (
                <div key={p.url} className="relative aspect-square border border-[var(--border)] overflow-hidden">
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                  {!busy && (
                    <button
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute top-0.5 right-0.5 bg-black/45 text-white p-0.5"
                      title={tr("Remove")}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {files.length === 1 && (
              <input
                data-testid="stock-upload-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={tr("Title (optional)")}
                className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
              />
            )}
            <input
              data-testid="stock-upload-keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder={
                files.length > 1
                  ? tr("Keywords for all these photos, comma separated")
                  : tr("Keywords, comma separated (e.g. ensenada, malecón, sunset)")
              }
              className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
            />
            <input
              data-testid="stock-upload-credit"
              value={credit}
              onChange={(e) => setCredit(e.target.value)}
              placeholder={tr("Preset caption / credit (e.g. Photo: Oliver Quintero)")}
              className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
            />
            {files.length > 1 && (
              <div className="text-[10px] text-[var(--ink-muted)]">{tr("Keywords and credit apply to every photo; fine-tune each one afterwards.")}</div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-between gap-3">
          <div className="text-xs text-[var(--ink-muted)]">
            {busy ? tr("Uploading {done} of {fileCount}…", { done, fileCount: files.length }) : tr("{fileCount} selected", { fileCount: files.length })}
          </div>
          <button
            data-testid="stock-upload-submit"
            onClick={uploadAll}
            disabled={!files.length || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{tr("Upload {value}", { value: files.length > 1 ? tr("{fileCount} photos", { fileCount: files.length }) : "" })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail / edit
// ---------------------------------------------------------------------------

function PhotoDetail({ photoId, propertyName, onClose }) {
  const qc = useQueryClient();
  const replaceRef = useRef(null);
  const photoQuery = useQuery({
    queryKey: ["stock-library", "photo", photoId],
    queryFn: () => api.getStockLibraryPhoto(photoId),
  });
  const photo = photoQuery.data;
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (photo) {
      setForm({
        title: photo.title || "",
        keywords: (photo.keywords || []).join(", "),
        credit: photo.credit || "",
        alt: photo.alt || "",
      });
    }
  }, [photo]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["stock-library"] });
  const onError = (e) => toast.error(e?.response?.data?.detail || e?.message || tr("Failed"));

  const save = useMutation({
    mutationFn: (extra = {}) =>
      api.patchStockLibraryPhoto(photoId, {
        title: form.title,
        keywords: splitKeywords(form.keywords),
        credit: form.credit,
        alt: form.alt,
        ...extra,
      }),
    onSuccess: (_d, extra) => {
      toast.success(extra?.status === "active" ? tr("Approved — now live in Newsdesk Stock") : tr("Saved"));
      refresh();
    },
    onError,
  });
  const replace = useMutation({
    mutationFn: (file) => api.replaceStockLibraryFile(photoId, file),
    onSuccess: () => {
      toast.success(tr("New version uploaded"));
      refresh();
    },
    onError,
  });
  const del = useMutation({
    mutationFn: () => api.deleteStockLibraryPhoto(photoId),
    onSuccess: () => {
      toast.success(tr("Photo deleted from Newsdesk Stock"));
      refresh();
      onClose();
    },
    onError,
  });

  const chips = form ? splitKeywords(form.keywords) : [];
  const f = photo?.file || {};
  const busy = save.isPending || replace.isPending || del.isPending;

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center px-4"
      onClick={() => !busy && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="stock-detail"
        className="w-full max-w-5xl bg-white border border-[var(--border)] shadow-xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="min-w-0">
            <div className="overline">{tr("Newsdesk Stock{value}", { value: photo?.status === "pending" ? tr(" · Pending") : "" })}
            </div>
            <h2 className="font-serif-display text-lg truncate">
              {photo?.title || f.filename || tr("Photo")}
            </h2>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!photo || !form ? (
          <div className="p-8 flex items-center gap-2 text-sm text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />{" "}{tr("Loading…")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_340px] overflow-y-auto">
            <div className="bg-[var(--secondary)] flex items-center justify-center p-3 min-h-[240px]">
              <img
                src={photo.web_url}
                alt={photo.alt || ""}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
            <div className="p-4 space-y-3 border-t md:border-t-0 md:border-l border-[var(--border)]">
              {photo.status === "pending" && (
                <div className="text-xs border border-[var(--border)] bg-[var(--secondary)] px-3 py-2 space-y-1.5">
                  <div>{tr("Added from")}{" "}
                    {photo.pending?.article_id ? (
                      <Link to={`/article/${photo.pending.article_id}`} className="underline">
                        this article
                      </Link>
                    ) : (
                      tr("an article")
                    )}
                    {" "}{tr("— goes live when it's published.")}</div>
                  <button
                    data-testid="stock-approve"
                    onClick={() => save.mutate({ status: "active" })}
                    disabled={busy}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--ink)] text-white disabled:opacity-40"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />{" "}{tr("Approve now")}</button>
                </div>
              )}
              <label className="block">
                {trRich("<0>Title</0>", null, [<span className="overline" />])}
                <input
                  data-testid="stock-edit-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1 w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
                />
              </label>
              <label className="block">
                {trRich("<0>Keywords</0>", null, [<span className="overline" />])}
                <textarea
                  data-testid="stock-edit-keywords"
                  value={form.keywords}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                  rows={3}
                  placeholder={tr("Comma separated")}
                  className="mt-1 w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none resize-y"
                />
                {chips.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {chips.map((k) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 border border-[var(--border)] bg-[var(--secondary)]">
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </label>
              <label className="block">
                {trRich("<0>Preset caption / credit</0>", null, [<span className="overline" />])}
                <input
                  data-testid="stock-edit-credit"
                  value={form.credit}
                  onChange={(e) => setForm({ ...form, credit: e.target.value })}
                  placeholder={tr("Photo: Oliver Quintero")}
                  className="mt-1 w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
                />
                {trRich("<0>Becomes the image legend whenever this photo is used in an article.</0>", null, [<span className="text-[10px] text-[var(--ink-muted)]" />])}
              </label>
              <label className="block">
                {trRich("<0>Description (alt text)</0>", null, [<span className="overline" />])}
                <textarea
                  data-testid="stock-edit-alt"
                  value={form.alt}
                  onChange={(e) => setForm({ ...form, alt: e.target.value })}
                  rows={2}
                  className="mt-1 w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none resize-y"
                />
              </label>

              <button
                data-testid="stock-save"
                onClick={() => save.mutate({})}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-40"
              >
                {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{tr("Save changes")}</button>

              <div className="text-[11px] text-[var(--ink-muted)] space-y-0.5 pt-2 border-t border-[var(--border)]">
                <div>
                  {tr("{width}×{height}px · {formatBytes} · {toUpperCase}{value}", { width: f.width, height: f.height, formatBytes: formatBytes(f.bytes), toUpperCase: String(f.ext || "").toUpperCase(), value: photo.version > 1 ? tr(" · version {version}", { version: photo.version }) : "" })}
                </div>
                <div>{tr("Uploaded {when}{value}{value2}", { when: formatDate(photo.created_at), value: photo.created_by ? tr(" by {created_by}", { created_by: photo.created_by }) : "", value2: propertyName(photo.property_id) ? ` · ${propertyName(photo.property_id)}` : "" })}
                </div>
                {photo.source_url && (
                  <div className="truncate">{trRich("From source: <0>{sourceHost}</0>", { sourceHost: sourceHost(photo.source_url) }, [<a href={photo.source_url} target="_blank" rel="noreferrer" className="underline" />])}
                    {photo.source_article_id && (
                      <>
                        {" "}{tr("· saved from")}{" "}
                        <Link to={`/article/${photo.source_article_id}`} className="underline">{tr("this article")}</Link>
                      </>
                    )}
                  </div>
                )}
                <div>{tr("Used {use_count} time{s}", { use_count: photo.use_count || 0, s: photo.use_count === 1 ? "" : "s" })}
                  {photo.last_used_article_id && (
                    <>
                      {" "}{tr("· last in")}{" "}
                      <Link to={`/article/${photo.last_used_article_id}`} className="underline">{tr("this article")}</Link>
                      {photo.last_used_at ? ` (${formatDate(photo.last_used_at)})` : ""}
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <input
                  ref={replaceRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) replace.mutate(file);
                    e.target.value = "";
                  }}
                />
                <button
                  data-testid="stock-replace"
                  onClick={() => replaceRef.current?.click()}
                  disabled={busy}
                  title={tr("Replace the file — keywords, credit and usage stay. Articles that already used it keep their copy.")}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40"
                >
                  {replace.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}{tr("Upload new version")}</button>
                <PasteImageButton
                  testId="stock-replace-paste"
                  label={tr("Paste new version")}
                  title={tr("Replace the file with the image on the clipboard — keywords, credit and usage stay.")}
                  // Button only: a stray Cmd/Ctrl+V must never replace a
                  // library photo.
                  hotkey={false}
                  onImage={(file) => replace.mutate(file)}
                  disabled={busy}
                  busy={replace.isPending}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40"
                />
                <a
                  href={photo.original_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)]"
                >
                  <Download className="w-3.5 h-3.5" />{" "}{tr("Original")}</a>
                <button
                  data-testid="stock-delete"
                  onClick={() =>
                    window.confirm(
                      tr("Delete this photo from Newsdesk Stock? Articles that already used it keep their copy."),
                    ) && del.mutate()
                  }
                  disabled={busy}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40"
                >
                  <Trash2 className="w-3.5 h-3.5" />{" "}{tr("Delete")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
